/* iter-p3-aa — Admin: Broadcast delivery audit. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send, RefreshCw, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function BroadcastsAdminPanel() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [resend,  setResend]  = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/broadcasts?page=1&page_size=100`,
        { withCredentials: true, timeout: 15000 });
      setRows(r.data?.broadcasts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load broadcasts.');
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const doResend = async (id) => {
    setResend(id);
    try {
      const r = await axios.post(`${API}/admin/broadcasts/${id}/resend-failures`,
        { dry_run: false }, { withCredentials: true, timeout: 30000 });
      const retried = r.data?.retried ?? 0;
      const still = r.data?.still_failed ?? 0;
      toast.success(`Retried ${retried} — ${still} still failing.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed.');
    } finally { setResend(null); }
  };

  const statusPill = (s) => {
    const c = s === 'sent' ? 'bg-emerald-100 text-emerald-900'
            : s === 'partial' ? 'bg-amber-100 text-amber-900'
            : s === 'failed' ? 'bg-rose-100 text-rose-900'
            : 'bg-muted text-muted-foreground';
    return <span className={`rounded px-1.5 py-0.5 text-[10.5px] font-semibold ${c}`}>{s || 'pending'}</span>;
  };

  return (
    <div className="space-y-4" data-testid="admin-broadcasts-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Radio className="h-4 w-4 text-primary" /> Author Broadcasts
          </h2>
          <p className="text-[12px] text-muted-foreground">
            Every fan-out from `POST /v1/authors/&#123;id&#125;/broadcast` — including auto release announcements when a book flips out of WIP.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="admin-broadcasts-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Reload
        </Button>
      </div>

      <div className="overflow-auto rounded-md border border-border" data-testid="admin-broadcasts-table-wrap">
        <table className="w-full text-left text-[12.5px]">
          <thead className="bg-muted/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 min-w-[240px]">Subject</th>
              <th className="px-3 py-2 w-32">Author</th>
              <th className="px-3 py-2 w-24">Audience</th>
              <th className="px-3 py-2 w-24">Status</th>
              <th className="px-3 py-2 w-20 text-right">Sent</th>
              <th className="px-3 py-2 w-20 text-right">Failed</th>
              <th className="px-3 py-2 w-40">Delivered</th>
              <th className="px-3 py-2 w-32"> </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`admin-broadcast-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.subject}</div>
                  <div className="text-[10.5px] text-muted-foreground">
                    {r.auto_publish_release ? 'Release announcement · ' : ''}{r.book_id || '—'}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono-stat text-[11px]">{r.author_id}</td>
                <td className="px-3 py-2 text-[11px] capitalize">{r.audience}</td>
                <td className="px-3 py-2">{statusPill(r.delivery_status)}</td>
                <td className="px-3 py-2 text-right font-mono-stat">{r.delivered_count ?? 0}</td>
                <td className={`px-3 py-2 text-right font-mono-stat ${r.failed_count ? 'text-rose-700 font-semibold' : ''}`}>
                  {r.failed_count ?? 0}
                </td>
                <td className="px-3 py-2 text-[11px] text-muted-foreground">
                  {r.delivered_at ? new Date(r.delivered_at).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2">
                  {(r.failed_count ?? 0) > 0 && (
                    <Button size="sm" variant="outline"
                            onClick={() => doResend(r.id)}
                            disabled={resend === r.id}
                            data-testid={`admin-broadcast-resend-${r.id}`}>
                      {resend === r.id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Send className="h-3.5 w-3.5" />}
                      Resend to failures
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                  No broadcasts yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
